'use client'

// Ambience — drives the daytime birdsong bed from the world's own daylight.
// Renders nothing. Audio can't start until a user gesture, so this keeps
// trying cheaply (a couple of times a second) until the context is running,
// then just tracks the level.

import { useEffect, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import { moodState } from '../../../lib/world/mood'
import { ensureBirdsong, setBirdsongDaylight } from '../../../lib/audio/ambience'
import { unlockAudio } from '../../../lib/audio/context'
import { loadFootsteps } from '../../../lib/audio/footsteps'

const UPDATE_INTERVAL = 0.5 // seconds; ambience doesn't need per-frame updates

export function Ambience() {
  const acc = useRef(0)

  // Unlock on the first gesture anywhere. First-person unlocks on its
  // pointer-lock click, but camera mode never clicks the canvas, so without
  // this the birdsong would only ever start for walkers.
  useEffect(() => {
    // Decoding needs no gesture, so fetch the step samples immediately rather
    // than waiting for the first canvas click.
    loadFootsteps()
    const once = () => { unlockAudio(); loadFootsteps() }
    window.addEventListener('pointerdown', once, { once: true })
    window.addEventListener('keydown', once, { once: true })
    return () => {
      window.removeEventListener('pointerdown', once)
      window.removeEventListener('keydown', once)
    }
  }, [])

  useFrame((_, dt) => {
    acc.current += dt
    if (acc.current < UPDATE_INTERVAL) return
    acc.current = 0
    ensureBirdsong()
    // Night is silent, day is full. nightness already smooths across dusk.
    setBirdsongDaylight(1 - moodState.nightness)
  })

  return null
}

export default Ambience
